import { useCallback, useEffect, useRef } from 'react';

/**
 * Returns a stable callback that delays invoking the wrapped function until
 * `delayMs` has elapsed since the last call. Used by the audit form for
 * auto-save on text inputs — every keystroke restarts the timer, the actual
 * PUT fires once the user pauses.
 *
 * Score radios don't use this; they fire immediately because a click is a
 * single discrete change.
 *
 * The hook cancels its pending timer on unmount so a half-typed comment
 * doesn't suddenly save after the user navigated away.
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delayMs: number,
): (...args: TArgs) => void {
  // Latest function ref so the debounced wrapper always sees the freshest
  // closure (the wrapped fn typically captures component state).
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return useCallback((...args: TArgs) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      fnRef.current(...args);
    }, delayMs);
  }, [delayMs]);
}
